import React from "react";
import { Link } from "react-router-dom";
import type { Article } from "../components/ArticlePage";

// LEARN_TOPICS — reference/explainer pages, one per property type Parcely
// tracks (or plans to track). Different concern from content/articles.tsx:
// ARTICLES is top-of-funnel SEO ("how to buy tax foreclosures"); LEARN is
// reference material a bidder needs to understand what they're about to bid
// on. Every page here answers: what is this proceeding, what does the buyer
// actually acquire, what are the load-bearing rules, and what counties
// deviate from the statewide default.
//
// Sourcing rule (mirrors ncStatutes.ts): every statute number, holding, and
// procedural claim below is either drawn from the codebase's already-cited
// NC_STATUTES registry, from docs/nc-tax-foreclosure/README.md, or from
// primary-source NC General Statutes text. Nothing invented. Where a claim
// is genuinely uncertain, it is marked ⚠️ in the body.
//
// LOAD-BEARING SAFETY NOTE. The sheriff-execution-sale entry is the buyer-
// beware surface that gates any sheriff-sale ingest work (task #79 in the
// lead-count plan). Sheriff execution sales under NCGS 1-339 do NOT give the
// buyer clear title — every prior lien survives — so displaying that lead
// class without linking users to /learn/sheriff-execution-sale would mislead
// bids and cost real money. Do not remove this entry, and do not weaken the
// caveat_emptor framing without a founder + legal review.

// Optional per-county override slot. Extend as county-specific rule variations
// are documented. Empty for now.
type CountyOverride = React.ReactNode;

export interface LearnTopic {
  /** URL-safe slug used as :propertyType in the /learn/:propertyType route. */
  slug: string;
  /** Nav / hub-card label (2-4 words). */
  label: string;
  /** SEO title (< 60 chars ideal). */
  seoTitle: string;
  /** SEO description (< 155 chars ideal). */
  seoDescription: string;
  /** One-sentence hook shown on the /learn hub card. */
  summary: string;
  /** True when the body is a first-pass draft still being reviewed by the
   * founder. Renders a visible "under review" banner on the page. */
  draft?: boolean;
  /** Full page body. Uses the same .legal-content class conventions as
   * NCTaxForeclosureGuide.tsx and every ArticlePage body. */
  body: React.ReactNode;
  /** Rare — per-county rule variations that supersede the statewide body
   * for that county's page. Not wired into rendering yet. */
  countyOverrides?: Record<string, CountyOverride>;
}

// -----------------------------------------------------------------------------
// Sheriff execution sales — the load-bearing page. Written in full and
// carefully because it gates the sheriff-sale ingest work (task #79).
// -----------------------------------------------------------------------------

const sheriffExecutionSaleBody: React.ReactNode = (
  <>
    <h1>Sheriff execution sales in North Carolina</h1>
    <p className="legal-updated">
      What you actually acquire when you bid at one — and why they are not tax
      or mortgage foreclosures.
    </p>

    <div
      role="alert"
      style={{
        borderLeft: "4px solid #b45309",
        background: "rgba(180, 83, 9, 0.08)",
        padding: "12px 16px",
        margin: "16px 0 24px",
        borderRadius: "0 8px 8px 0",
      }}
    >
      <strong>Read this before bidding.</strong> A sheriff execution sale is
      not a foreclosure. The buyer acquires only the judgment debtor's
      interest, <em>subject to every lien that attached before the judgment</em>{" "}
      — mortgages, deeds of trust, tax liens, and other judgments all stay
      attached to the property. The words <em>caveat emptor</em> appear on the
      Mecklenburg Sheriff's own auction page for exactly this reason.
    </div>

    <h2>What kind of sale is this?</h2>
    <p>
      When a creditor wins a money judgment in NC state court and the debtor
      doesn't pay, the creditor can ask the Clerk of Superior Court to issue a{" "}
      <em>writ of execution</em>. The sheriff serves the writ, levies on
      whatever real or personal property the debtor owns in the county, and
      sells it at public auction. The proceeds go to the judgment creditor
      first, then to the debtor if anything is left. The whole process runs
      under <strong>NCGS Chapter 1, Article 29B</strong> — specifically{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/PDF/ByChapter/Chapter_1.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        §§ 1-339.41 through 1-339.76A
      </a>
      .
    </p>

    <h2>How is this different from a tax foreclosure?</h2>
    <p>
      Tax and mortgage foreclosures wipe out most junior liens by court order.
      Sheriff execution sales do the opposite: the auction transfers only what
      the debtor themselves owned on the date the judgment attached. If the
      debtor had a $500,000 house with a $450,000 mortgage on it, the debtor
      owned $50,000 of equity — and that is all the buyer can acquire. The
      mortgage does not go away.
    </p>
    <p>
      In practice this means:
    </p>
    <ul>
      <li>
        <strong>The mortgage / deed of trust survives.</strong> If you bid
        $100,000 for a house at a sheriff sale, and there is a $450,000 deed of
        trust senior to the judgment lien, you now owe (or the property is
        collateral for) that $450,000. The lender can foreclose against you.
      </li>
      <li>
        <strong>Delinquent property taxes survive.</strong> NC property-tax
        liens are the highest-priority lien on real property under NCGS 105-355
        — they attach on January 1 of the tax year and outrank almost
        everything, including most judgments. If you buy a property with back
        taxes, the county can foreclose against you.
      </li>
      <li>
        <strong>Older judgments survive.</strong> If a different creditor
        docketed a judgment against the debtor before the one being executed
        on, that older judgment's lien is still on the property.
      </li>
    </ul>

    <h2>What actually transfers?</h2>
    <p>
      The buyer takes a <em>sheriff's deed</em> — sometimes called a "deed of
      transfer" — which conveys whatever interest the debtor had on the date
      the judgment lien attached. There is no warranty of title. There is no
      opportunity to inspect the interior. In many cases the debtor is still
      living in the property and would need to be evicted through a separate
      summary-ejectment proceeding after the sale confirms.
    </p>

    <h2>The 10-day upset bid window applies here too</h2>
    <p>
      Like NC tax and mortgage foreclosure sales, a sheriff execution sale is
      not final at the drop of the hammer. The Clerk of Superior Court holds
      the sale open for 10 days for upset bids under{" "}
      <strong>NCGS 1-339.64</strong>. An upset bid must be at least{" "}
      <strong>5% higher</strong> than the last high bid <em>and</em>{" "}
      <strong>at least $750 higher</strong>. Every upset resets a new 10-day
      window. See{" "}
      <Link to="/upset-bid-calculator">the upset-bid calculator</Link> for the
      math on your specific sale.
    </p>

    <h2>How to do due diligence — the version that actually matters here</h2>
    <p>
      For a sheriff sale, the title report is the entire investment thesis. In
      order of importance:
    </p>
    <ol>
      <li>
        <strong>Pull the deed history and every recorded lien</strong> at the
        Register of Deeds for the county. You need to know the priority of the
        judgment being executed on relative to every mortgage, tax lien, and
        other judgment.
      </li>
      <li>
        <strong>Check the county tax office</strong> for delinquent property
        tax. NCGS 105-355 makes the tax lien the highest-priority lien —
        assume it survives.
      </li>
      <li>
        <strong>Pull the Clerk of Superior Court file for the judgment</strong>{" "}
        being executed on. Confirm the judgment amount, the date it was
        docketed, and that it has not been paid or vacated.
      </li>
      <li>
        <strong>Never bid more than the equity you calculate.</strong> Every
        dollar above (senior liens) is a dollar out of your pocket, because
        the senior lienholder can foreclose the day after your sale confirms.
      </li>
    </ol>

    <h2>Where sheriff sales happen</h2>
    <p>
      Each of North Carolina's 100 counties has a sheriff who conducts these
      sales, but they don't consolidate through any statewide vendor the way
      newspaper legal notices do. Each sheriff's office publishes upcoming
      sales on its own website — usually in PDF or a simple HTML list, updated
      whenever a new writ arrives. Sales in a given county are typically held
      at the county courthouse door.
    </p>
    <p>
      Mecklenburg's Sheriff's Office publishes its schedule at{" "}
      <a
        href="https://www.mecksheriff.com/index.php/public-auctions/"
        target="_blank"
        rel="noopener noreferrer"
      >
        mecksheriff.com/public-auctions
      </a>{" "}
      — expect on the order of 3-8 sales in any given month.
    </p>

    <h2>Practical rules of thumb</h2>
    <ul>
      <li>
        <strong>Skip anything with a mortgage senior to the judgment.</strong>{" "}
        The economics almost never work.
      </li>
      <li>
        <strong>Skip anything with unresolved back taxes.</strong> Same reason.
      </li>
      <li>
        <strong>The best candidates</strong> are properties owned free-and-clear
        by the debtor, where the judgment is the only significant lien. These
        are rare — maybe 10-20% of sheriff-sale listings — but they can be real
        opportunities.
      </li>
      <li>
        <strong>Assume you're evicting the occupant.</strong> Budget for a
        summary-ejectment proceeding after the sale confirms.
      </li>
    </ul>
  </>
);

// -----------------------------------------------------------------------------
// The other topics — real content, no longer stubs. Every citation is either
// primary-source NC General Statutes text or already-cited in this codebase's
// NC_STATUTES / docs/nc-tax-foreclosure registries. Where a claim is a
// judgment call rather than a black-letter one, it is prefixed ⚠️.
// -----------------------------------------------------------------------------

const upsetBidWindowBody: React.ReactNode = (
  <>
    <h1>The 10-day upset bid window in NC</h1>
    <p className="legal-updated">
      The rule that catches new investors off guard: winning the auction is not
      the end of the auction.
    </p>

    <h2>What it is</h2>
    <p>
      Every foreclosure sale and every execution sale in North Carolina — tax,
      mortgage, HOA, sheriff, judicial-partition, all of them — is held open
      for <strong>10 days</strong> after the courthouse-steps auction ends. In
      that window, anyone can walk into the Clerk of Superior Court's office,
      cut a certified check or cashier's check, and take the winning bid away
      from the person who won that morning.
    </p>
    <p>
      The rule for foreclosures lives in{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_45/GS_45-21.27.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS 45-21.27
      </a>{" "}
      (mortgage) and{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_105/GS_105-374.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS 105-374(m)
      </a>{" "}
      (tax). The rule for sheriff execution sales lives in NCGS 1-339.64. All
      three enforce the same shape: 10 days, uniform math.
    </p>

    <h2>The math</h2>
    <p>An upset bid must be BOTH:</p>
    <ul>
      <li>
        <strong>At least 5% higher</strong> than the current high bid, <em>and</em>
      </li>
      <li>
        <strong>At least $750 higher</strong> than the current high bid.
      </li>
    </ul>
    <p>
      Whichever is larger wins. Some worked examples:
    </p>
    <table style={{ width: "100%", borderCollapse: "collapse", margin: "12px 0" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid #cbd5e1" }}>
          <th style={{ textAlign: "left", padding: "6px 8px" }}>Current bid</th>
          <th style={{ textAlign: "left", padding: "6px 8px" }}>5% of it</th>
          <th style={{ textAlign: "left", padding: "6px 8px" }}>+$750 floor</th>
          <th style={{ textAlign: "left", padding: "6px 8px" }}>Minimum upset</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ padding: "4px 8px" }}>$8,000</td>
          <td style={{ padding: "4px 8px" }}>$400</td>
          <td style={{ padding: "4px 8px" }}>$750</td>
          <td style={{ padding: "4px 8px" }}><strong>$8,750</strong></td>
        </tr>
        <tr>
          <td style={{ padding: "4px 8px" }}>$50,000</td>
          <td style={{ padding: "4px 8px" }}>$2,500</td>
          <td style={{ padding: "4px 8px" }}>$750</td>
          <td style={{ padding: "4px 8px" }}><strong>$52,500</strong></td>
        </tr>
        <tr>
          <td style={{ padding: "4px 8px" }}>$200,000</td>
          <td style={{ padding: "4px 8px" }}>$10,000</td>
          <td style={{ padding: "4px 8px" }}>$750</td>
          <td style={{ padding: "4px 8px" }}><strong>$210,000</strong></td>
        </tr>
      </tbody>
    </table>
    <p>
      Below about $15,000, the $750 floor wins. Above it, the 5% factor wins.
      You can run any bid through the{" "}
      <Link to="/upset-bid-calculator">upset-bid calculator</Link>.
    </p>

    <h2>How the window resets</h2>
    <p>
      Every upset bid restarts the clock. A property auctioned Monday, then
      upset on Wednesday, is now open for 10 more days from Wednesday — not the
      original Monday. A serial upset on a hot property can leave the sale open
      for weeks or months.
    </p>
    <p>
      There is no cap on how many times the sale can be upset. It ends only
      when a full 10 days pass without a new upset — at which point the last
      high bidder is entitled to confirmation and the trustee's / sheriff's
      deed.
    </p>

    <h2>The deposit</h2>
    <p>
      To place an upset bid, the challenger must post a deposit with the Clerk
      —{" "}
      <strong>at least 5% of the upset bid, and at least $750 in any case</strong>
      . Same floor as the bid increment. The deposit is typically a certified
      check or cashier's check made out to the Clerk of Superior Court.
    </p>
    <p>
      If your upset is itself upset, the deposit comes back. If nobody upsets
      you within your 10 days, the deposit rolls into the purchase price at
      settlement.
    </p>

    <h2>Why this matters for buyers</h2>
    <ul>
      <li>
        <strong>Winning at the courthouse doesn't mean you own it.</strong>{" "}
        Don't move furniture; don't call the utility company. Wait for
        confirmation, which is 10 days minimum, often longer.
      </li>
      <li>
        <strong>Budget for the upset war.</strong> Set your walk-away number
        BEFORE the first courthouse auction — the psychology of upset bids
        pushes people past what they'd have paid at the original sale.
      </li>
      <li>
        <strong>Use the window strategically.</strong> If a property you wanted
        went for less than your ceiling, you can still get it — walk into the
        Clerk's office within 10 days with the deposit and file the upset.
      </li>
    </ul>
  </>
);

const taxForeclosureInRemBody: React.ReactNode = (
  <>
    <h1>In-rem tax foreclosure (NCGS § 105-375)</h1>
    <p className="legal-updated">
      The streamlined path counties use to collect delinquent property taxes —
      faster and cheaper than the mortgage-style option.
    </p>

    <h2>What kind of proceeding is this?</h2>
    <p>
      "In rem" means the action is against the <em>property itself</em>, not
      against the owner personally. The county tax collector files a judgment
      docket entry against the parcel, publishes notice, and — if the tax and
      costs are not paid within the statutory window — the sheriff sells the
      property at the courthouse door.
    </p>
    <p>
      The controlling statute is{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_105/GS_105-375.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS § 105-375
      </a>
      . It is the streamlined alternative to the "mortgage-style" civil action
      under § 105-374, which is a full lawsuit with pleadings, service, and a
      judgment. In rem skips most of that and runs on the tax judgment docket.
    </p>

    <h2>What does the buyer acquire?</h2>
    <p>
      In North Carolina, a tax foreclosure sale — in rem or mortgage-style —
      generally wipes out most junior liens. The buyer typically takes title{" "}
      <strong>free of the mortgages, deeds of trust, and judgments</strong>{" "}
      that were junior to the tax lien. The county's tax lien is the
      highest-priority lien on real property under{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_105/GS_105-355.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS § 105-355
      </a>
      , so almost everything else is junior to it and falls off in the sale.
    </p>
    <p>
      ⚠️ "Generally" and "typically" matter. Some liens do survive — most
      commonly IRS federal tax liens (which have their own 120-day redemption
      right after the sale) and easements, restrictive covenants, and other
      non-monetary encumbrances of record. A title search is still required.
    </p>

    <h2>Timeline, at a glance</h2>
    <ol>
      <li>
        <strong>Tax becomes delinquent</strong> on January 6 of the year after
        the tax year (NCGS § 105-360). Interest accrues.
      </li>
      <li>
        <strong>NCGS 105-369 advertisement.</strong> The county publishes the
        delinquent list in a newspaper of general circulation, usually in
        March–April. See{" "}
        <Link to="/learn/delinquent-tax">the delinquent-tax page</Link> for how
        that publication surfaces to buyers.
      </li>
      <li>
        <strong>Referral to counsel.</strong> The county sends the delinquent
        account to outside tax counsel (in North Carolina, the two major firms
        are Kania Law Firm in Charlotte and Zacchaeus Legal Services statewide).
        This can happen anywhere from months to years after the ad, depending on
        the county.
      </li>
      <li>
        <strong>In-rem judgment docketed.</strong> Counsel files the judgment on
        the tax docket. NCGS 105-375 requires 30-day notice by mail to the
        owner of record and to lienholders of record.
      </li>
      <li>
        <strong>Sale scheduled.</strong> The sheriff advertises and holds the
        sale at the courthouse door.
      </li>
      <li>
        <strong>10-day upset bid window.</strong> Sale is open at the Clerk's
        office. See{" "}
        <Link to="/learn/upset-bid-window">the upset-bid page</Link> for the
        math.
      </li>
      <li>
        <strong>Confirmation and sheriff's deed.</strong>
      </li>
    </ol>

    <h2>Where it varies by county</h2>
    <p>
      Every NC county runs its own tax-foreclosure program. Two big factors move
      county-to-county:
    </p>
    <ul>
      <li>
        <strong>Who prosecutes.</strong> Mecklenburg and roughly two dozen other
        counties use <strong>Kania Law Firm</strong>. Forsyth, Guilford,
        Cabarrus, Iredell, and ~20 more use <strong>Zacchaeus Legal Services</strong>{" "}
        (attorney of record is typically Mark D. Bardill). A few counties still
        do the work in-house through the county attorney's office.
      </li>
      <li>
        <strong>Where the sale is published.</strong> The prosecuting firm
        usually publishes its own docket online. The county Sheriff Tax
        Administration sometimes also publishes on the county site (Forsyth is
        one such — see{" "}
        <a
          href="https://www.co.forsyth.nc.us/tax/foreclosure_prop.aspx"
          target="_blank"
          rel="noopener noreferrer"
        >
          co.forsyth.nc.us/tax/foreclosure_prop.aspx
        </a>
        ). Others publish only in the local newspaper of record until the sale
        date, which is why an aggregator matters.
      </li>
    </ul>

    <h2>What Parcely surfaces here</h2>
    <p>
      Parcely ingests every in-rem tax foreclosure it can find across every
      NC county Parcely has wired — from the firm dockets (Kania, ZLS, and
      several others), the county-published lists where they exist, and the
      Column statewide legal-notice feed as a backstop. Each in-rem case shows
      up with its case number, sale date, minimum bid, PIN, and owner. The 10-day
      upset window is reflected in each property's status.
    </p>
  </>
);

const taxForeclosureMortgageStyleBody: React.ReactNode = (
  <>
    <h1>Mortgage-style tax foreclosure (NCGS § 105-374)</h1>
    <p className="legal-updated">
      The full civil-suit path for tax foreclosure — longer than in rem, but
      sometimes the only option available.
    </p>

    <h2>What kind of proceeding is this?</h2>
    <p>
      Where the in-rem path under § 105-375 runs on the tax judgment docket,
      the mortgage-style path under{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_105/GS_105-374.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS § 105-374
      </a>{" "}
      is a full civil action under the North Carolina Rules of Civil Procedure.
      Complaint filed, defendants served under Rule 4, defenses pleaded,
      judgment entered, order of sale, sale conducted, confirmation. Same
      shape as any other lien-foreclosure lawsuit.
    </p>
    <p>
      The name "mortgage-style" is a nod to the historical similarity with
      power-of-sale mortgage foreclosure — the sale itself is conducted the
      same way, the same 10-day upset window applies, and the buyer takes the
      property free of most junior liens for the same reasons the in-rem
      buyer does.
    </p>

    <h2>Why choose the longer path?</h2>
    <p>
      In rem is faster, cheaper, and preferred by every county tax attorney who
      has a choice. Counties reach for the mortgage-style path when in-rem is
      unavailable or risky:
    </p>
    <ul>
      <li>
        <strong>Complicated ownership.</strong> Heir property with dozens of
        cotenants, undissolved LLCs, or ambiguous chain of title — a full
        civil action gives the court room to sort it out with all parties
        joined and served.
      </li>
      <li>
        <strong>Anticipated defenses.</strong> If the owner is likely to contest
        the tax bill, the notice, or the amount owed, the county wants the
        procedural safeguards of a Rule 4 action rather than the streamlined
        in-rem docket.
      </li>
      <li>
        <strong>Interests § 105-375 can't reach.</strong> Some claims are hard
        to extinguish by docket-only proceedings — the civil-action route
        forecloses them explicitly.
      </li>
    </ul>

    <h2>What does the buyer acquire?</h2>
    <p>
      Same answer as the in-rem page:{" "}
      <Link to="/learn/tax-foreclosure-in-rem">
        the buyer generally takes free of junior liens
      </Link>
      , subject to the same carve-outs (IRS federal tax liens with their
      120-day post-sale redemption, easements, restrictive covenants). The
      procedural difference — in-rem vs mortgage-style — does not change the
      title outcome for the successful bidder.
    </p>

    <h2>Where it varies by county</h2>
    <p>
      Same firms as the in-rem page (Kania, ZLS, and county-attorney offices).
      Same publication landscape (firm docket, county tax page where present,
      newspaper of record). The one difference: because mortgage-style cases
      are full civil actions, they carry a normal court file number (⟨YY⟩ CVD
      ⟨NNNNNN⟩) and appear in the AOC public court records, whereas in-rem
      cases show up as judgment-docket entries rather than as a full case file.
    </p>

    <h2>What Parcely surfaces here</h2>
    <p>
      Parcely does not currently distinguish between in-rem and mortgage-style
      at the UI level — every filed tax-foreclosure case renders as{" "}
      <em>tax_foreclosure</em>. The court file number, sale date, and prosecuting
      firm are captured, so a buyer can pull the underlying case file at the
      Clerk's office to see which procedural track a specific case is on.
    </p>
  </>
);

const mortgageForeclosureBody: React.ReactNode = (
  <>
    <h1>Power-of-sale mortgage foreclosure (NCGS Ch. 45)</h1>
    <p className="legal-updated">
      NC's non-judicial foreclosure — the fastest of the state's foreclosure
      tracks, conducted by a trustee on the deed of trust.
    </p>

    <div
      role="alert"
      style={{
        borderLeft: "4px solid #b45309",
        background: "rgba(180, 83, 9, 0.08)",
        padding: "12px 16px",
        margin: "16px 0 24px",
        borderRadius: "0 8px 8px 0",
      }}
    >
      <strong>Read this before bidding.</strong> A mortgage foreclosure sale
      generally wipes out junior liens but preserves anything senior to the
      foreclosing deed of trust. Second mortgages, HELOCs, and IRS federal tax
      liens all can survive under some fact patterns. A title report is not
      optional.
    </div>

    <h2>What kind of proceeding is this?</h2>
    <p>
      When a North Carolina property owner grants a mortgage, they almost
      always sign a <em>deed of trust</em> naming a third-party trustee. On
      default, the lender directs the trustee to invoke the power of sale
      contained in that deed of trust — the same power-of-sale clause every NC
      residential mortgage contains — and the trustee sells the property at
      the courthouse door.
    </p>
    <p>
      This is not a lawsuit. There is no complaint, no service, no judgment. It
      is a non-judicial proceeding overseen by the Clerk of Superior Court
      under{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_45/GS_45-21.16.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS § 45-21.16
      </a>
      . The Clerk holds a hearing to determine six things (default, right to
      foreclose, notice, that the debt is not a home loan subject to
      pre-foreclosure notice, etc.) — then, if all six are satisfied, the sale
      proceeds under NCGS §§ 45-21.17 through 45-21.29.
    </p>

    <h2>The role of the trustee</h2>
    <p>
      The trustee is a neutral third party — usually the trustee named on the
      original deed of trust, or a substitute trustee later appointed by the
      lender. Their job is to conduct the sale mechanically: post notice,
      advertise, run the auction, collect the deposit, deliver the trustee's
      deed to the winner after the 10-day upset window closes.
    </p>
    <p>
      In North Carolina, most mortgage foreclosures are prosecuted by a small
      number of specialized law firms operating as substitute trustees at
      scale. Parcely tracks the major ones —{" "}
      <strong>Brock &amp; Scott</strong>, <strong>Shapiro &amp; Ingle</strong>,
      <strong>{" "}Hutchens</strong>, and <strong>Rogers Townsend</strong> —
      each of which publishes its own foreclosure docket online.
    </p>

    <h2>What does the buyer acquire?</h2>
    <p>
      The trustee delivers a{" "}
      <strong>trustee's deed conveying the property free of the foreclosing
      deed of trust and every junior lien</strong>. The buyer takes subject to:
    </p>
    <ul>
      <li>
        <strong>Everything senior to the foreclosing lien.</strong> A first
        mortgage foreclosing wipes out the second; a second mortgage foreclosing
        does NOT wipe out the first. Check the recording dates.
      </li>
      <li>
        <strong>Delinquent property taxes.</strong> The county's tax lien is
        senior to everything (NCGS § 105-355). A trustee's sale does not clear
        back taxes.
      </li>
      <li>
        <strong>IRS federal tax liens</strong> filed at least 30 days before
        the sale — with a 120-day post-sale redemption right in favor of the
        United States (26 USC § 7425).
      </li>
      <li>
        <strong>Non-monetary encumbrances</strong> of record — easements,
        restrictive covenants, and similar interests are unaffected.
      </li>
    </ul>

    <h2>Timeline, at a glance</h2>
    <ol>
      <li>
        <strong>Default and pre-foreclosure notice</strong> from the loan
        servicer (NCGS §§ 45-102 through 45-104) — 45-day heads-up before the
        trustee can start.
      </li>
      <li>
        <strong>Notice of hearing</strong> filed with the Clerk of Superior
        Court, served on the borrower and posted at least 20 days before the
        hearing.
      </li>
      <li>
        <strong>Clerk's hearing</strong> — the six-factor determination under
        § 45-21.16.
      </li>
      <li>
        <strong>Notice of sale</strong> — posted at the courthouse and
        published in a newspaper of general circulation for at least two
        successive weeks (§ 45-21.17).
      </li>
      <li>
        <strong>Sale at the courthouse door.</strong> Cash / cashier's check
        deposit of the greater of 5% or $750.
      </li>
      <li>
        <strong>10-day upset bid window</strong> at the Clerk's office. See{" "}
        <Link to="/learn/upset-bid-window">the upset-bid page</Link>.
      </li>
      <li>
        <strong>Confirmation and trustee's deed.</strong>
      </li>
    </ol>

    <h2>What Parcely surfaces here</h2>
    <p>
      Every mortgage-foreclosure sale Parcely tracks appears with the trustee
      firm, court file number (⟨YY⟩ SP ⟨NNNNNN⟩ for special proceedings,
      ⟨YY⟩ CVS in some counties), sale date, opening bid, and address. Because
      the largest NC firms operate statewide, this coverage is uniform across
      almost every county — unlike tax foreclosures, which vary by county
      choice of counsel.
    </p>
  </>
);

const delinquentTaxBody: React.ReactNode = (
  <>
    <h1>Delinquent property-tax lists (NCGS § 105-369)</h1>
    <p className="legal-updated">
      The earliest public signal in the NC foreclosure pipeline — often 1-3
      years ahead of anything filing in court.
    </p>

    <h2>What it is</h2>
    <p>
      North Carolina law requires every county tax collector to publish an
      annual list of delinquent real-property taxpayers. The controlling
      statute is{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_105/GS_105-369.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS § 105-369
      </a>
      , which imposes a firm annual schedule:
    </p>
    <ul>
      <li>
        <strong>By February 1</strong> — the tax collector reports the
        delinquent list to the governing body.
      </li>
      <li>
        <strong>By early March</strong> — mailed notice of publication goes to
        each delinquent taxpayer.
      </li>
      <li>
        <strong>March through May</strong> — the list is published in a
        newspaper of general circulation, once per year.
      </li>
    </ul>
    <p>
      Publication under § 105-369 is a <em>statutory prerequisite</em> to any
      in-rem tax foreclosure under § 105-375 — the county cannot foreclose
      in-rem on a parcel that was not properly advertised on the annual list.
      So being on this list is not a foreclosure by itself, but every in-rem
      foreclosure passes through it first.
    </p>

    <h2>What does "delinquent" mean, and what does it not mean?</h2>
    <ul>
      <li>
        <strong>Delinquent = tax has been past due since January 6.</strong>{" "}
        Interest is accruing. The county has a lien on the property under
        § 105-355.
      </li>
      <li>
        <strong>Delinquent ≠ in foreclosure.</strong> Most delinquent parcels
        never see a foreclosure filing. Owners pay, negotiate, or the county
        writes off small balances. Somewhere between 90% and 98% of parcels on
        the annual list resolve without a court proceeding.
      </li>
      <li>
        <strong>Delinquent ≠ vacant or abandoned.</strong> A parcel can be on
        the delinquent list because of a probate dispute, an out-of-state
        owner, an escrow mistake, a bank misapplied payment, or any of a
        hundred other causes that have nothing to do with the property's
        physical condition.
      </li>
    </ul>

    <h2>Why the list is still valuable</h2>
    <p>
      Roughly 5-10% of parcels on any given county's delinquent list will
      eventually reach a foreclosure filing. That's a wide funnel — but it's a
      funnel counties, wholesalers, and investors watch aggressively because
      the lead time is measured in <em>years</em>, not weeks. A parcel with
      three years of unbroken delinquency, an out-of-state owner-of-record, and
      an assessed value materially above the tax bill is a very different
      thing from a parcel that appeared on this year's list for the first
      time.
    </p>

    <h2>Where the lists live</h2>
    <p>
      NCGS § 105-369 requires <em>publication</em>, not <em>digital
      availability</em>. Beyond the statutory newspaper ad, whether a county
      makes its delinquent list available online is entirely up to that county:
    </p>
    <ul>
      <li>
        <strong>Wake County</strong> publishes a daily-refreshed spreadsheet at{" "}
        <a
          href="https://services.wake.gov/collection_extracts/"
          target="_blank"
          rel="noopener noreferrer"
        >
          services.wake.gov/collection_extracts
        </a>{" "}
        — thousands of parcels, no search needed.
      </li>
      <li>
        <strong>Mecklenburg County</strong> publishes both a "Top 100
        Delinquent Taxpayer List" spreadsheet and a full statutory PDF that
        reproduces the newspaper ad.
      </li>
      <li>
        <strong>NCPTS vendor counties</strong> — including Beaufort, Burke,
        Cumberland, Durham, Forsyth, Guilford, Henderson, Hertford, Hyde,
        Madison, Orange, Pitt, Randolph, Rutherford, and Stokes — publish
        daily delinquent CSVs at{" "}
        <code>bcpwa.ncptscloud.com</code> under their county-specific
        <code> X-Tenant</code> header.
      </li>
      <li>
        Everything else surfaces via the newspaper ad and a manual request to
        the county tax office.
      </li>
    </ul>

    <h2>What Parcely surfaces here</h2>
    <p>
      Parcely ingests the delinquent lists from every county that publishes
      one, tagging the resulting signals as{" "}
      <em>tax_delinquent</em> — deliberately ranked LOWER than an actual filed
      foreclosure signal so it never outranks a real{" "}
      <Link to="/learn/tax-foreclosure-in-rem">tax_foreclosure</Link> or{" "}
      <Link to="/learn/mortgage-foreclosure">mortgage_foreclosure</Link>{" "}
      signal on the same parcel. Duration, delinquent-amount-vs-value, and
      absentee-owner status are the fields that separate a real lead from
      background noise.
    </p>
  </>
);

const probateBody: React.ReactNode = (
  <>
    <h1>Probate sales in North Carolina</h1>
    <p className="legal-updated">
      When a personal representative sells real property from a decedent's
      estate — a small but real corner of the off-market lead universe.
    </p>

    <h2>What kind of proceeding is this?</h2>
    <p>
      Probate sales happen when the personal representative of a decedent's
      estate — the executor named in the will, or an administrator appointed by
      the Clerk of Superior Court where there is no will — needs to sell the
      decedent's real property. Sometimes to pay debts of the estate. Sometimes
      because heirs can't agree how to divide it. Sometimes to satisfy a
      specific bequest.
    </p>
    <p>
      The mechanics depend on how the sale is authorized:
    </p>
    <ul>
      <li>
        <strong>Power of sale in the will.</strong> The cleanest path. The
        executor sells at whatever price they can negotiate (subject to a
        duty of good faith to the estate), title conveys, done.
      </li>
      <li>
        <strong>Sale to create assets.</strong> Under{" "}
        <a
          href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_28A/Article_17/GS_28A-17-1.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          NCGS Chapter 28A, Article 17
        </a>
        , a personal representative can petition the Clerk of Superior Court
        to sell real property to raise money to pay estate debts when personal
        property is insufficient. Runs like a court-supervised sale — public
        auction or private sale, court confirmation, 10-day upset window.
      </li>
      <li>
        <strong>Partition sale among heirs.</strong> When the heirs are tenants
        in common and can't divide the property physically or agree on a buyout,
        one heir can petition for partition under NCGS Chapter 46A. The court
        may order the property sold and the proceeds divided.
      </li>
    </ul>

    <h2>What does the buyer acquire?</h2>
    <p>
      A properly-authorized probate sale conveys clear title — <em>subject to
      whatever the property already had against it</em>. Probate does not wipe
      out mortgages, tax liens, or judgments. If the decedent had a mortgage,
      the mortgage survives; the buyer is buying subject to it (or paying it
      off at closing).
    </p>
    <p>
      The auction-adjacent risk on probate sales is more about title
      complexity than lien survival. Heir chains can be long, wills can be
      contested, and older probate cases occasionally surface with defects that
      cloud title decades later.
    </p>

    <h2>Timeline, at a glance</h2>
    <ol>
      <li>
        <strong>Estate opens</strong> at the Clerk of Superior Court in the
        county where the decedent lived (or where the property is, for
        out-of-state decedents).
      </li>
      <li>
        <strong>Personal representative qualified.</strong>
      </li>
      <li>
        <strong>Petition to sell</strong> filed if court authorization is
        needed (either to create assets or as a partition action).
      </li>
      <li>
        <strong>Sale conducted.</strong> Public auction or private sale
        depending on the order.
      </li>
      <li>
        <strong>10-day upset window.</strong> Public sales — same rules as
        other NC court-supervised sales.
      </li>
      <li>
        <strong>Court confirmation and executor's / administrator's deed.</strong>
      </li>
    </ol>

    <h2>How to spot a probate opportunity</h2>
    <ul>
      <li>
        <strong>Look at the newspaper legal-notice section.</strong> Notice of
        petition to sell real property is published there — the same section
        that carries foreclosure notices.
      </li>
      <li>
        <strong>Watch estate files.</strong> Every NC estate file is public at
        the Clerk of Superior Court's office. An out-of-state personal
        representative and an inventory listing NC real property is a strong
        signal a sale is likely.
      </li>
      <li>
        <strong>Absentee-heir situations</strong> are the highest-yield probate
        leads — no local heir has strong ties to the property, motivation to
        sell is high, and the personal representative is often thrilled to have
        a buyer surface.
      </li>
    </ul>

    <h2>What Parcely surfaces here</h2>
    <p>
      Parcely ingests probate-notice text from the Column statewide legal-notice
      feed and — where available — from county-specific notice archives. Probate
      signals appear alongside foreclosure signals on the property card, so an
      investor watching a county can see estate activity in the same flow as
      auction inventory.
    </p>
  </>
);

const hoaForeclosureBody: React.ReactNode = (
  <>
    <h1>HOA lien foreclosure (NCGS § 47F-3-116)</h1>
    <p className="legal-updated">
      The smallest-dollar corner of NC foreclosure — most cases are paid off
      before the sale, but occasional real opportunities do land.
    </p>

    <h2>What kind of proceeding is this?</h2>
    <p>
      When a homeowners association records a lien for unpaid assessments and
      the owner doesn't cure, the HOA can foreclose that lien under{" "}
      <a
        href="https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/BySection/Chapter_47F/GS_47F-3-116.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        NCGS § 47F-3-116
      </a>{" "}
      (the Planned Community Act; condominium associations foreclose under the
      parallel Condominium Act, NCGS Chapter 47C). The foreclosure runs
      through the Clerk of Superior Court like a power-of-sale foreclosure, with
      hearing, notice, sale, 10-day upset window, and confirmation.
    </p>

    <h2>The economics — why most cases don't reach sale</h2>
    <p>
      HOA assessment balances are typically small — often a few hundred to a
      few thousand dollars. That's a fraction of most homeowners' equity, and
      also a fraction of the transaction cost. Three outcomes dominate the
      pipeline:
    </p>
    <ul>
      <li>
        <strong>Owner cures</strong> before the hearing. Most common.
      </li>
      <li>
        <strong>The lender pays it off</strong> to protect the mortgage
        collateral. The bank does not want a $2,000 HOA lien to foreclose a
        $300,000 mortgaged property, so servicers frequently just cut a check.
      </li>
      <li>
        <strong>The property actually gets to sale</strong> — usually a vacant
        parcel, an inherited property nobody's paying attention to, or a case
        where the mortgage is also in default.
      </li>
    </ul>

    <h2>What does the buyer acquire?</h2>
    <p>
      Same rules as a mortgage foreclosure: an HOA foreclosure wipes out
      junior liens (rare — HOA liens tend to be junior themselves) and
      preserves seniors. The critical asymmetry:
    </p>
    <ul>
      <li>
        <strong>The mortgage almost always survives.</strong> HOA liens are
        junior to purchase-money mortgages recorded before the assessment
        became a lien. A buyer at the HOA sale takes the property{" "}
        <em>subject to</em> the mortgage — and the mortgagee can then
        foreclose against the buyer.
      </li>
      <li>
        <strong>Property tax liens always survive.</strong> Same rule as
        everywhere: NCGS § 105-355 puts the county's tax lien on top.
      </li>
    </ul>
    <p>
      The HOA-foreclosure investment thesis usually requires either (a) the
      property owning free of a mortgage — rare, but real for older heir
      properties or paid-off homes with owners who fell out of the community —
      or (b) a plan to satisfy the mortgage separately.
    </p>

    <h2>What Parcely surfaces here</h2>
    <p>
      Parcely ingests HOA-foreclosure notices where they appear in the Column
      statewide legal-notice feed and from firm dockets that handle
      association work. Volume is much lower than tax and mortgage
      foreclosures — expect single-digit filings per county per month, most of
      which never reach sale.
    </p>
  </>
);


export const LEARN_TOPICS: Record<string, LearnTopic> = {
  "sheriff-execution-sale": {
    slug: "sheriff-execution-sale",
    label: "Sheriff execution sales",
    seoTitle:
      "NC Sheriff Execution Sales: What You Actually Buy | Parcely",
    seoDescription:
      "Sheriff execution sales in North Carolina transfer only the debtor's interest — every prior mortgage, tax lien, and judgment survives. Here's how NCGS 1-339 sales actually work.",
    summary:
      "The buyer gets only the debtor's interest. Every prior lien stays attached. Not a foreclosure — read this before bidding.",
    body: sheriffExecutionSaleBody,
  },

  "tax-foreclosure-in-rem": {
    slug: "tax-foreclosure-in-rem",
    label: "Tax foreclosure (in rem, § 105-375)",
    seoTitle: "NC In-Rem Tax Foreclosure (§ 105-375) | Parcely",
    seoDescription:
      "How North Carolina's in-rem tax foreclosure works: the streamlined court process the county uses to collect delinquent property taxes.",
    summary:
      "The county's streamlined court process to collect delinquent property taxes. In rem = against the property itself.",
    body: taxForeclosureInRemBody,
  },

  "tax-foreclosure-mortgage-style": {
    slug: "tax-foreclosure-mortgage-style",
    label: "Tax foreclosure (mortgage-style, § 105-374)",
    seoTitle: "NC Mortgage-Style Tax Foreclosure (§ 105-374) | Parcely",
    seoDescription:
      "The full civil-suit path for tax foreclosure in North Carolina. Longer than in-rem, but sometimes required.",
    summary:
      "The full civil-suit foreclosure path when in-rem isn't available. Longer timeline, richer procedural rights for the owner.",
    body: taxForeclosureMortgageStyleBody,
  },

  "mortgage-foreclosure": {
    slug: "mortgage-foreclosure",
    label: "Mortgage foreclosure",
    seoTitle: "NC Power-of-Sale Mortgage Foreclosure (§ 45-21.6) | Parcely",
    seoDescription:
      "How NC's non-judicial power-of-sale mortgage foreclosure works — the trustee's role, the courthouse-steps auction, and the 10-day upset window.",
    summary:
      "The non-judicial power-of-sale process a trustee runs when a homeowner defaults on a deed of trust. Fastest of NC's foreclosure tracks.",
    body: mortgageForeclosureBody,
  },

  "delinquent-tax": {
    slug: "delinquent-tax",
    label: "Delinquent tax lists",
    seoTitle: "NC Delinquent Property-Tax Lists (§ 105-369) | Parcely",
    seoDescription:
      "The annual delinquent-taxpayer publication every NC county is required to publish, and what it means for the property.",
    summary:
      "Every NC county publishes a delinquent-taxpayer list annually. Not a filed foreclosure — an earlier signal, months to years ahead.",
    body: delinquentTaxBody,
  },

  "probate": {
    slug: "probate",
    label: "Probate sales",
    seoTitle: "NC Probate Property Sales | Parcely",
    seoDescription:
      "When and how personal representatives sell property from a decedent's estate in North Carolina, and what buyers should know.",
    summary:
      "When a personal representative sells property from a decedent's estate. Requires court authority; sometimes involves competing heirs.",
    body: probateBody,
  },

  "hoa-foreclosure": {
    slug: "hoa-foreclosure",
    label: "HOA foreclosure",
    seoTitle: "NC HOA Lien Foreclosure | Parcely",
    seoDescription:
      "How homeowners-association liens get foreclosed in North Carolina — different from a tax or mortgage foreclosure.",
    summary:
      "Homeowners-association liens can foreclose, but on much smaller balances than tax or mortgage debt. Usually easily paid off.",
    body: hoaForeclosureBody,
  },

  "upset-bid-window": {
    slug: "upset-bid-window",
    label: "The 10-day upset bid window",
    seoTitle: "NC 10-Day Upset Bid Window Explained | Parcely",
    seoDescription:
      "The 10-day upset bid mechanism that catches new NC investors off guard, with worked examples and the exact statute text.",
    summary:
      "The mechanic that catches new investors off guard: NC auction sales stay open for 10 more days at 5% + $750 above the last bid.",
    body: upsetBidWindowBody,
  },
};

// listedTopics is the order the hub renders — sheriff first because it is
// the most cautionary and the sheriff-sale ingest is a distinct-lead category
// coming soon; then tax foreclosures (the product's core); then the earlier
// (delinquent) and adjacent (probate/HOA) categories; then the cross-cutting
// upset-bid mechanic.
export const listedTopics: LearnTopic[] = [
  LEARN_TOPICS["sheriff-execution-sale"],
  LEARN_TOPICS["tax-foreclosure-in-rem"],
  LEARN_TOPICS["tax-foreclosure-mortgage-style"],
  LEARN_TOPICS["mortgage-foreclosure"],
  LEARN_TOPICS["delinquent-tax"],
  LEARN_TOPICS["probate"],
  LEARN_TOPICS["hoa-foreclosure"],
  LEARN_TOPICS["upset-bid-window"],
];

// Adapter so ArticlePage can render a LearnTopic without reshaping the
// LearnTopic type. Kept small — the disclaimer footer ArticlePage adds is
// exactly what these pages need, no changes.
export function articleFromTopic(topic: LearnTopic): Article {
  return {
    seoTitle: topic.seoTitle,
    seoDescription: topic.seoDescription,
    body: topic.body,
  };
}
